import axios from 'axios';
import { api } from './api';
import { storage } from './storage';
import { createError } from './error-handler';
import type {
  LoginCredentials,
  LoginResponseUser,
  ResponseUser,
  User,
  CreateUserRequest,
  AuthResponse,
  PaginatedResponse,
} from '@/types';

export const authService = {
  // Login with email and password
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    try {
      const response = await api.post('/auth/login', {
        email: credentials.email,
        password: credentials.password,
      });

      const responseData = response.data as LoginResponseUser;

      // Extract token
      const accessToken = responseData.token;

      // Map login response to normalized ResponseUser format
      const user: ResponseUser = {
        id: responseData.userId,
        email: responseData.email,
        firstName: responseData.firstName,
        lastName: responseData.lastName,
        companyId: responseData.companyId,
        type: responseData.type,
        profileImage: null,
        expirationTime: responseData.expirationTime,
      };

      if (!accessToken) {
        throw new Error('Token bulunamadı');
      }

      // Store token, expiration time, and user data using centralized storage
      storage.setToken(accessToken);
      storage.setTokenExpiration(responseData.expirationTime);
      storage.setUser(user);

      return { accessToken, user };
    } catch (error) {
      console.error('Login error:', error);
      throw createError(error, 'Giriş sırasında bir hata oluştu');
    }
  },
  // Create new user
  async createUser(user: CreateUserRequest): Promise<number> {
    try {
      // const hashedPassword = await bcrypt.hash(user.password, 10);
      const response = await api.post<number>('/users', {
        ...user,
        password: user.password,
        companyId: user.companyId ?? 1,
        type: user.type ?? 0,       // backend default enum için
        cityId: user.cityId ?? 0,
        districtId: user.districtId ?? 0,
        profileImage: user.profileImage ?? "",
        language: user.language ?? "tr",
      });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error(" Axios error response:", error.response?.data);
        throw createError(error, 'Kullanıcı oluşturulamadı');
      }

      console.error("Non-Axios error:", error);
      throw new Error("Kullanıcı oluşturma sırasında bir hata oluştu");
    }
  },
  //get all user
  async getAllUsers(index: number, size: number): Promise<PaginatedResponse<User>> {
    try {
      const response = await api.get<PaginatedResponse<User>>('/users', {
        params: { index, size }
      });

      return response.data;
    } catch (error) {
      console.error('GetUsers error:', error);
      throw createError(error, 'Kullanıcıları getirirken bir hata oluştu');
    }
  },
  //update user
  async updateUser(id: number, userData: {
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber?: string | null;
    cityId?: number | null;
    districtId?: number | null;
    profileImage?: string | null;
  }): Promise<User> {
    try {
      const response = await api.put<User>(`/users/${id}`, {
        id,                               // backend command için gerekli
        firstName: userData.firstName,
        lastName: userData.lastName,
        email: userData.email,
        phoneNumber: userData.phoneNumber ?? null,
        cityId: userData.cityId ?? 0,
        districtId: userData.districtId ?? 0,
        profileImage: userData.profileImage ?? null,
      });

      return response.data;
    } catch (error) {
      console.error("UpdateUser error:", error);
      throw createError(error, "Kullanıcı güncellenirken bir hata oluştu");
    }
  },
  //delete user
  async deleteUser(id: number): Promise<void> {
    try {
      await api.delete(`/users/${id}`);
    } catch (error) {
      console.error("DeleteUser error:", error);
      throw createError(error, "Kullanıcı silme sırasında bir hata oluştu");
    }
  },

  // Logout - Call backend and clear local storage
  async logout(): Promise<void> {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      console.error('Server logout error:', error);
      // Continue with local cleanup even if server logout fails
    } finally {
      // Always clear local storage regardless of server response
      storage.clearAuth();
    }
  },

  // Get current user from localStorage (returns login user format)
  getCurrentUser(): ResponseUser | null {
    return storage.getUser();
  }
  ,
  // Get current user via API (for TanStack Query)
  async fetchCurrentUser(): Promise<ResponseUser> {
    try {
      // Backend'ten kullanıcıyı çek
      const response = await api.get<ResponseUser>("/users/profile");
      return response.data;
    } catch (error) {
      // Eğer API başarısız olursa localStorage fallback
      const user = this.getCurrentUser();
      if (!user) {
        throw new Error("No user found");
      }
      return user;
    }
  },

  // Check if user is authenticated
  isAuthenticated(): boolean {
    return storage.hasAuth();
  },

  // Get stored token
  getToken(): string | null {
    return storage.getToken();
  },

  // Update user profile
  async updateProfile(userData: {
    firstName?: string;
    lastName?: string;
    password?: string;
  }): Promise<ResponseUser> {
    try {
      const response = await api.put<ResponseUser>('/users/change-profile', {
        name: userData.firstName,
        surname: userData.lastName,
        password: userData.password ?? "",
      });
      return response.data;
    } catch (error) {
      throw createError(error, 'Profil güncellenirken bir hata oluştu');
    }
  }
  ,
};
